import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceScreen,
  PreferenceSubmit,
  PreferenceUpload,
} from '../../components/Preference'
import { useMe } from '../../lib/auth-client'
import { fetcher } from '../../lib/swr'

type FollowRow = {
  tenantId: number
  tenantName: string
  createdAt: string
}

export default function HouseholdSettings() {
  const { data: me, mutate: mutateMe } = useMe()
  const follows = useSWR<FollowRow[]>(
    me?.user && !me.user.isAnonymous ? '/api/follows' : null,
    fetcher
  )
  const [tenantName, setTenantName] = useState('')
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  /** Bust img cache after upload/clear. */
  const [imageRev, setImageRev] = useState(0)

  useEffect(() => {
    if (me?.tenant?.name != null) setTenantName(me.tenant.name)
  }, [me?.tenant?.name])

  const tenantId = me?.tenant?.id ?? null
  const hasImage = Boolean(me?.tenant?.hasImage)

  async function saveTenant() {
    setBusy(true)
    try {
      const res = await fetch('/api/tenants', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: tenantName }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Bewaren mislukt')
        return
      }
      setTenantName(data.tenant.name)
      toast.success('Bewaard')
      await mutateMe()
    } finally {
      setBusy(false)
    }
  }

  async function uploadImage(file: File) {
    if (tenantId == null) return
    if (!file.type.startsWith('image/')) {
      toast.error('Kies een afbeeldingsbestand')
      return
    }
    setUploading(true)
    try {
      const res = await fetch(`/api/tenants/${tenantId}/image`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Upload mislukt')
        return
      }
      toast.success('Avatar gezet')
      setImageRev((n) => n + 1)
      await mutateMe()
    } finally {
      setUploading(false)
    }
  }

  async function removeImage() {
    if (tenantId == null || !hasImage) return
    setUploading(true)
    try {
      const res = await fetch(`/api/tenants/${tenantId}/image`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Verwijderen mislukt')
        return
      }
      toast.success('Avatar verwijderd')
      setImageRev((n) => n + 1)
      await mutateMe()
    } finally {
      setUploading(false)
    }
  }

  async function unfollow(followedTenantId: number) {
    const res = await fetch(`/api/follows/${followedTenantId}`, {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      toast.error(data.message || 'Ontvolgen mislukt')
      return
    }
    await follows.mutate()
    toast.success('Niet meer gevolgd')
  }

  const followRows = follows.data ?? []

  return (
    <PreferenceScreen title="Huishouden" backHref="/settings">
      <PreferenceCategory filled>
        <PreferenceInput
          title="Naam"
          value={tenantName}
          onChange={setTenantName}
          placeholder="Wilgenstraat"
        />
        <PreferenceSubmit
          title="Bewaren"
          onPress={saveTenant}
          disabled={busy || !tenantName.trim()}
        />
      </PreferenceCategory>

      {tenantId != null ? (
        <PreferenceCategory title="Avatar" filled>
          {hasImage ? (
            <div className="px-5 py-3 flex items-center gap-3">
              <img
                src={`/api/tenants/${tenantId}/image?v=${imageRev}`}
                alt=""
                className="size-14 rounded-full object-cover ring-1 ring-white/15"
              />
              <p className="text-sm text-white/55">
                Zichtbaar bij jullie recepten in andermans kookboek.
              </p>
            </div>
          ) : (
            <PreferenceInfo
              title="Geen avatar"
              summary="Optioneel. Verschijnt als rond icoon bij jullie naam."
            />
          )}
          <PreferenceUpload
            title={hasImage ? 'Andere afbeelding' : 'Kies afbeelding'}
            summary="JPEG, PNG of WebP · max 2 MB"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onFileSelect={uploadImage}
            isUploading={uploading}
          />
          {hasImage ? (
            <PreferenceButton
              title="Verwijder avatar"
              onPress={removeImage}
              disabled={uploading || busy}
              isDestructive
            />
          ) : null}
        </PreferenceCategory>
      ) : null}

      <PreferenceCategory title="Gevolgde huishoudens" filled>
        {followRows.length === 0 ? (
          <PreferenceInfo
            title="Nog niemand"
            summary="Volg via een uitnodigingslink om hun lijsten te zien."
          />
        ) : (
          followRows.map((f) => (
            <PreferenceButton
              key={f.tenantId}
              title={f.tenantName}
              summary="Tik om te ontvolgen"
              onPress={() => void unfollow(f.tenantId)}
            />
          ))
        )}
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
